import apiEndpoint from '../config/data';
import { getAuthHeader } from '../../utils/tokenService';

/**
 * Self-serve cancellation submit for the "Subscription & Billing" page
 * (PROF-1191 / PROF-1193). Posts the merchant's cancellation reasons + optional
 * free-text detail to the Profit Assist BFF, which records the cancellation in
 * Starfish, emails the merchant's owners, and notifies the CS inbox.
 *
 * Base host mirrors the trials read in `useSubscriptionStatus.js`: when
 * `REACT_APP_PROFIT_ASSIST_BFF_URL` is unset the path is left relative and
 * resolved against `apiEndpoint` (`REACT_APP_API_ENDPOINT`) below. Auth is the
 * Dashboard Okta session cookie (`credentials: 'include'`) plus the same
 * Authorization header the shared fetch helper sends.
 */
const CANCELLATION_API_BASE = process.env.REACT_APP_PROFIT_ASSIST_BFF_URL || '';

// BLID must match BL-XXXX-XXXX-XXXX (alphanumeric groups). Guarded client-side
// so a malformed cookie fails fast instead of hitting the BFF with a 400.
const BLID_PATTERN = /^BL-[A-Za-z0-9]+-[A-Za-z0-9]+-[A-Za-z0-9]+$/;

// The BFF caps detail at 1000 chars; trim client-side to match the contract.
const MAX_DETAIL_LENGTH = 1000;

/**
 * Error thrown by {@link cancelSubscription} carrying the HTTP status and a
 * coarse category the modal branches on:
 *  - `validation` (400): show inline form errors.
 *  - `unauthenticated` (401): session expired. The caller (modal) surfaces a
 *    re-login prompt; this service only throws and never redirects.
 *  - `not_found` (404): "No active subscription to cancel." Do NOT retry.
 *  - `retryable` (429/502/5xx/network): show a retry-able message.
 *  - `unknown`: anything else.
 */
export class CancellationError extends Error {
  constructor(message, { status = null, category = 'unknown', fields } = {}) {
    super(message);
    this.name = 'CancellationError';
    this.status = status;
    this.category = category;
    this.fields = fields;
  }
}

const categoryForStatus = status => {
  if (status === 400) return 'validation';
  if (status === 401) return 'unauthenticated';
  if (status === 404) return 'not_found';
  if (status === 429 || status >= 500) return 'retryable';
  return 'unknown';
};

/**
 * Builds the BFF cancellation URL (`POST /api/v1/subscriptions/:blid/cancel`),
 * per the PROF-1193 contract. Mirrors the `/api/v1` prefix used by the trials
 * read in `useSubscriptionStatus.js`. No trailing slash.
 */
export const buildCancellationPath = blid =>
  `${CANCELLATION_API_BASE}/api/v1/subscriptions/${encodeURIComponent(blid)}/cancel`;

/**
 * Submits a cancellation request for the given merchant.
 *
 * Intentionally does NOT use `fetchWithAuth` (which force-logs the user out on
 * a 401): a failed cancellation must not tear down the session. Non-2xx
 * responses reject with a {@link CancellationError} so the caller can branch on
 * `.category` (inline vs. retry vs. no-retry).
 *
 * @param {string} blid - Merchant BLID (BL-XXXX-XXXX-XXXX).
 * @param {object} payload
 * @param {'trial'|'paid'} payload.subscriptionType - FE-supplied; BE won't infer.
 * @param {string[]} [payload.reasons] - Unique verbatim reason strings; may be
 *   empty (a no-reason submit is accepted by the BFF).
 * @param {string} [payload.detail] - Free-text; required iff "Other" is chosen.
 *   Omitted from the body when empty (the BFF 400s on an explicit `null`).
 * @returns {Promise<object|null>} Parsed response body (blid, status, reasons,
 *   detail, cancelledAt, effectiveEndsAt), or null when the body is empty.
 *   Note: actor identity is captured server-side from the Okta session — the FE
 *   does NOT send an actor/email field.
 */
export const cancelSubscription = async (
  blid,
  { subscriptionType, reasons, detail } = {}
) => {
  if (!blid || !BLID_PATTERN.test(blid)) {
    throw new CancellationError(
      'cancelSubscription: a valid BLID (BL-XXXX-XXXX-XXXX) is required',
      { category: 'validation' }
    );
  }

  // `reasons` are de-duplicated verbatim labels. The BFF accepts an empty array
  // (a no-reason submit is valid), so normalize a missing / non-array value to
  // `[]` rather than rejecting client-side.
  const uniqueReasons = Array.isArray(reasons) ? [...new Set(reasons)] : [];

  const trimmedDetail =
    typeof detail === 'string' ? detail.trim().slice(0, MAX_DETAIL_LENGTH) : '';

  const body = {
    subscriptionType,
    reasons: uniqueReasons,
    // Omit `detail` entirely when empty — the BFF 400s on an explicit `null`.
    ...(trimmedDetail ? { detail: trimmedDetail } : {}),
  };

  const path = buildCancellationPath(blid);
  const url = path.startsWith('http') ? path : `${apiEndpoint}${path}`;
  const authHeader = getAuthHeader();

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    // Network failure — safe to retry (cancel is idempotent on the BFF).
    throw new CancellationError(
      'Cancellation request failed to reach the server',
      { category: 'retryable' }
    );
  }

  if (!response.ok) {
    // Pull the BFF's `message` array (names failing fields on a 400) if present.
    const errorBody = await response.json().catch(() => null);
    const fields = Array.isArray(errorBody?.message)
      ? errorBody.message
      : undefined;
    throw new CancellationError(
      `Cancellation request failed (${response.status})`,
      {
        status: response.status,
        category: categoryForStatus(response.status),
        fields,
      }
    );
  }

  // Success bodies are JSON; tolerate an empty body (e.g. a bare 204).
  return response.json().catch(() => null);
};
